// StepPayment — STAFF FORK (ADR-076 §9 copy-fork).
//
// A deliberate copy of portal-subscriber/src/obw/steps/StepPayment.tsx, used by
// the staff-mounted B2S (Upgrade) journey so it runs IN PLACE on the nexa-admin
// dashboard (StaffObwModal) instead of opening the subscriber portal. The two
// copies drift independently; behavioural fixes are applied to both.
//
// Phase 5c.5 (ADR-030 D2 Step 4) — plan summary panel on left, Stripe-element
// stub on right. Real Stripe Elements arrive post-Phase-6 — this step renders a
// card-styled stub that gates the "Continue" CTA. No charge occurs (ADR-073 Am.1).
import { useState, useEffect } from 'react';
import {
  TIER_NAMES,
  COMMITMENT_LABELS,
  getCataloguePricing,
  displayPrice,
  fmtUsd,
  type CommitmentTerm,
} from '../../lib/promo-codes';
import { fetchCatalogue, type Catalogue } from '../../lib/catalogue';
import type { StepProps } from '../OBWWizard';
import { portalOriginFor } from '../portal-origin';

type Tier = 'T1' | 'T2' | 'T3';

interface PaymentPreviewResponse {
  tier: string;
  basePrice: number;
  discountedPrice: number;
  promoCode?: string;
  promoDescription?: string;
  note?: string;
}

export function StepPayment({ data, accountId, onNext }: StepProps) {
  const tier = (data?.selectedTier as Tier) ?? null;
  const promoCode = (data?.promoCode as string) ?? null;
  const commitment = (data?.commitment as CommitmentTerm) ?? 'annual';
  /**
   * Resolved OUTSIDE the effect, deliberately. `portalOriginFor(data)` inside the effect would
   * make the whole `data` object a dependency, so the request below would re-fire on every
   * keystroke anywhere in the journey — for a call that is documented dead (see the ⛔ note).
   * A string is a stable dependency; the object it came from is not.
   */
  const portalOrigin = portalOriginFor(data);

  const [preview, setPreview] = useState<PaymentPreviewResponse | null>(null);
  const [previewError, setPreviewError] = useState('');
  // The journey's zone's catalogue — the same price the plan step showed (7 October 2026: this fork
  // priced from a static 50%-off table). ⛔ No fallback price: until it loads the summary says so.
  const [catalogue, setCatalogue] = useState<Catalogue | null>(null);
  const [catalogueFailed, setCatalogueFailed] = useState(false);
  useEffect(() => {
    let cancelled = false;
    fetchCatalogue(portalOrigin)
      .then((cat) => { if (!cancelled) setCatalogue(cat); })
      .catch(() => { if (!cancelled) setCatalogueFailed(true); });
    return () => { cancelled = true; };
  }, [portalOrigin]);

  // Promo-description lookup only — headline numbers come from the commitment
  // pricing model below (display-only; real Stripe is LAUNCH/5d-4). Best-effort:
  // for the account-less staff mount (accountId '') the promo description simply
  // won't load — the client-side pricing fallback still renders.
  useEffect(() => {
    if (!tier) return;
    let cancelled = false;
    setPreviewError('');
    void (async () => {
      try {
        // ⛔ KNOWN DEAD, AND DELIBERATELY LEFT (ADR-107 §4a). This call carries no
        // Bearer, and `/portal/payment/preview` sits behind the SUBSCRIBER Cognito
        // authorizer — which rejects staff-pool JWTs by design (ADR-018
        // cross-portal isolation). So it 401s and the priced summary falls back to
        // its local computation.
        //
        // It is not fixable from this fork: it would need a new `/admin/*` route, a
        // new Cedar action and a CDK/IAM change, to fetch a PRICE DISPLAY on an
        // account-less journey a consultant is watching. Documented here rather
        // than silently retried, because the whole class of defect this ADR closes
        // was calls that failed invisibly.
        const res = await fetch(`${portalOrigin}/portal/payment/preview`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ accountId, tier, promoCode }),
        });
        if (res.ok && !cancelled) {
          setPreview((await res.json()) as PaymentPreviewResponse);
        }
      } catch {
        if (!cancelled) setPreviewError('Could not load price preview.');
      }
    })();
    return () => { cancelled = true; };
  }, [accountId, tier, promoCode, portalOrigin]);

  const pricing = tier && catalogue ? getCataloguePricing(catalogue, tier, commitment) : null;
  const tierName = tier ? TIER_NAMES[tier] : null;
  // D8 — no local promo math: the promo capability is not built, and a code from the local table
  // previewed a discount the server never billed. Only the catalogue's (server-resolved) prices show.
  const promoDiscount = 0;
  const shown = pricing ? displayPrice(pricing, promoDiscount) : null;
  const effectiveMonthly = fmtUsd(shown?.effectiveMonthly ?? 0);
  const upfrontTotal = fmtUsd(shown?.termTotal ?? 0);
  const showStrike = !!shown?.showStrike;

  if (!tier) {
    return (
      <div className="obw-step-content">
        <h2 className="obw-title">Payment</h2>
        <p className="obw-subtitle">No plan selected. Go back and choose a plan to continue.</p>
      </div>
    );
  }

  return (
    <div className="obw-step-content">
      <h2 className="obw-title">Payment</h2>
      <p className="obw-subtitle">
        Add a card to activate your subscription. We use Stripe — your card details are never sent
        to or stored on Nexanomics servers.
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(220px, 1fr) 2fr', gap: '24px', marginTop: '24px' }}>
        {/* LEFT: Plan summary */}
        <div className="obw-auth-box" style={{ margin: 0 }}>
          <p className="obw-form-section" style={{ marginTop: 0 }}>Order summary</p>
          <p style={{ fontSize: '18px', fontWeight: 700, color: 'var(--text)', margin: '0 0 6px' }}>{tierName}</p>
          {!pricing ? (
            <p style={{ fontSize: '14px', color: 'var(--text-dim)', margin: 0 }} role="status" aria-live="polite" data-testid="obw-payment-price-loading">
              {catalogueFailed ? 'We couldn’t load the price just now — go back a step and try again.' : 'Loading your price…'}
            </p>
          ) : (<>
          <p style={{ fontSize: '14px', color: 'var(--text-dim)', margin: '0 0 14px' }}>
            {COMMITMENT_LABELS[commitment]} commitment{pricing?.isEa ? ' — Early Adopter price' : ''}
          </p>
          <div>
            <p style={{ margin: '0 0 4px', fontSize: '24px', fontWeight: 800, color: 'var(--text)' }}>
              {showStrike && (
                <span style={{ textDecoration: 'line-through', color: 'var(--text-dim)', fontSize: '16px', marginRight: '8px' }}>
                  ${fmtUsd(pricing.rackMonthly)}
                </span>
              )}
              ${effectiveMonthly}<span style={{ fontSize: '14px', color: 'var(--text-dim)', fontWeight: 500 }}>/mo</span>
            </p>
            <p style={{ fontSize: '14px', color: 'var(--text-dim)', margin: '0 0 4px' }}>
              {commitment === 'monthly'
                ? `Billed $${effectiveMonthly} each month`
                : `Billed $${upfrontTotal} once for ${pricing.months} months`}
            </p>
            {pricing?.isEa && (
              <p style={{ fontSize: '14px', color: 'var(--success)', fontWeight: 600, margin: 0 }}>
                ✓ Renews at ${fmtUsd(pricing.rackMonthly)}/mo (rack rate) after the committed term
              </p>
            )}
            {preview?.promoDescription && (
              <p style={{ fontSize: '14px', color: 'var(--success)', fontWeight: 600, margin: '4px 0 0' }}>
                ✓ {preview.promoDescription}
              </p>
            )}
          </div>
          </>)}
          {previewError && (
            <p style={{ fontSize: '14px', color: 'var(--danger)', marginTop: '8px' }}>{previewError}</p>
          )}
        </div>

        {/* RIGHT: Stripe element stub */}
        <div className="obw-auth-box" style={{ margin: 0 }}>
          <p className="obw-form-section" style={{ marginTop: 0 }}>Card details</p>
          <div className="obw-form-group">
            <label className="obw-form-label" htmlFor="stub-card-num">Card number</label>
            <input
              id="stub-card-num"
              className="obw-form-input"
              type="text"
              placeholder="4242 4242 4242 4242"
              disabled
              aria-describedby="stripe-stub-note"
            />
          </div>
          <div className="obw-form-grid-2">
            <div className="obw-form-group">
              <label className="obw-form-label" htmlFor="stub-card-exp">Expiry</label>
              <input id="stub-card-exp" className="obw-form-input" type="text" placeholder="MM/YY" disabled />
            </div>
            <div className="obw-form-group">
              <label className="obw-form-label" htmlFor="stub-card-cvc">CVC</label>
              <input id="stub-card-cvc" className="obw-form-input" type="text" placeholder="123" disabled />
            </div>
          </div>
          <p id="stripe-stub-note" className="obw-form-hint" style={{ marginTop: '12px' }}>
            <strong>Phase 5b billing stub.</strong> Stripe Elements integration arrives in Phase 6.
            Your card will not be charged during this setup step.
          </p>
        </div>
      </div>

      <div className="portal-wizard-nav">
        <button
          type="button"
          className="obw-btn-primary"
          onClick={() => onNext({ paymentStubbed: true })}
          disabled={!tier}
        >
          Continue
        </button>
      </div>
    </div>
  );
}
